import { useCallback, useEffect, useMemo, useRef, useState } from 'react'
import { Icon } from '../ui/Icon'
import { Msg, Turn, WorkingFold } from '../agents/transcriptBlocks'
import { parseInbound } from '../agents/inbound'
import { useSenderResolution } from '../agents/useSenderResolution'
import { SharedViewContext, type SharedViewer } from '../agents/sharedView'
import { productForCli } from '../agents/agentKinds'
import { useCleanSearch } from '../agents/search/useCleanSearch'
import { turnSummary, worthCollapsing } from '../agents/turns'
import { onAgentShare } from '../servers/commandStream'
import {
  BffError,
  getSharedStatus,
  getSharedTranscript,
  shareErrText,
  type IncomingAgentShare,
  type SharedStatus,
} from '../transport/relay'
import type { TranscriptBlock } from '../transport/api'
import { clearSharedEchoes, onSharedKick, setSharedLive, useSharedEchoes } from './sharedSession'

/** One page of conversation, as Clean View pages it. */
const PAGE = 120
/** Start fetching before the top is reached — Clean View's own distance. */
const BACKFILL_TRIGGER_PX = 400
/**
 * THE LEASE ON "WORKING" (useConvBusy's rule, for the same reason): the cloud tells this screen
 * when the agent starts or finishes a turn, but a lost poke would leave it working for ever, and
 * nothing else can reach a friend's screen when the agent stops. So while it works and the page is
 * visible, the claim is asked again once per lease — and the conversation read with it, so the
 * turn's steps arrive while it runs as they do in your own agent's view — never while it does not.
 */
const LEASE_MS = 20_000
/** A refusal that means the share itself is over, not that this one read failed. */
const OVER = new Set(['share_gone', 'share_expired', 'not_friends', 'share_not_accepted', 'agent_gone'])

/**
 * Text as compared, not as shown: whitespace folded, and the cloud's own rewrites of a friend's
 * words undone — ＠ for @ (so it attaches no file), ‹ › for < > in frame-like tags. Without this a
 * message holding an @ never matched its own echo and showed twice (verification round 4).
 */
const norm = (x: string) =>
  x
    .replace(/＠/g, '@')
    .replace(/‹/g, '<')
    .replace(/›/g, '>')
    // An @-pinged agent of the reader's lands as its link's address (phase 2): compared by its label.
    .replace(/<ping-agent:[^:>\s]+(?::([^>]*))?>/g, '@$1')
    .replace(/\s+/g, ' ')
    .trim()

/** Has this block landed the words the reader just sent? (Either identity: framed or as typed.) */
export function holds(b: TranscriptBlock, sent: string): boolean {
  if (b.kind !== 'user') return false
  const said = parseInbound(b.text)?.body ?? b.text ?? ''
  return norm(said).startsWith(norm(sent).slice(0, 60))
}

export const productOf = (cli: string | null) =>
  cli ? productForCli(cli === 'antigravity' ? 'agy' : cli === 'claude-code' ? 'claude' : cli) : null

/**
 * A FRIEND'S AGENT, ON THE STAGE — owner, 2026-10-17: 朋友 Agent 的 session ui 可以用右開的方式。
 * 完全參照目前一般 Agent 的 UI. It opens the way your own agent does (pushed over the rail on a
 * phone), and this is its conversation: Clean View's own frame (#claudeOverlay), its turns, its
 * echo and its working head — App's header, pill and composer sit around it as they do for yours.
 *
 * Fed from the share's narrow road (routes_agent_share): the cloud reads this one agent's
 * conversation, holds back what came before the share unless the sharer allowed it, and carries
 * the reader's words in. The search reads only what is on this screen — never the machine's whole
 * conversation, which would reach past what the share shows.
 */
export function SharedAgentPanel({
  share,
  selfEmail,
  selfLabel,
}: {
  share: IncomingAgentShare
  selfEmail: string | null
  selfLabel: string
}) {
  const id = share.share_id
  const owner = share.from.name || share.from.email || 'Your friend'
  const name = share.agent.label || share.agent.session
  const hash = share.agent.hash ?? ''
  const machine = share.agent.machine || 'their machine'
  const product = productOf(share.agent.cli)

  const [blocks, setBlocks] = useState<TranscriptBlock[]>([])
  const [loaded, setLoaded] = useState(false)
  const [older, setOlder] = useState({ from: 0, pending: 0, hidden: false })
  const [status, setStatus] = useState<SharedStatus | null>(null)
  const [gone, setGone] = useState<string | null>(null)
  const [err, setErr] = useState<string | null>(null)
  const at = useRef<{ file: string; total: number } | null>(null)
  const flight = useRef<Promise<void> | null>(null)
  const again = useRef(false)
  const log = useRef<HTMLDivElement>(null)
  const stick = useRef(true)
  const echo = useSharedEchoes(id)

  const fail = useCallback((e: unknown) => {
    if (e instanceof BffError && e.code && OVER.has(e.code)) setGone(shareErrText(e))
    else setErr(shareErrText(e))
  }, [])

  const read = useCallback(async () => {
    const cur = at.current
    // `tail` rides along always: when the file has changed (a /clear) the machine answers with
    // a page of the new one rather than all of it (review of 2026-10-17).
    const r = await getSharedTranscript(id, cur ? { file: cur.file, have: cur.total, tail: PAGE } : { tail: PAGE })
    at.current = { file: r.file, total: r.total }
    if (r.mode === 'append' && cur) {
      if (r.blocks.length) setBlocks((b) => [...b, ...r.blocks])
    } else {
      setBlocks(r.blocks)
      setOlder({ from: r.from, pending: r.pending, hidden: r.history_hidden })
    }
    if (r.blocks.length) clearSharedEchoes(id, (t) => r.blocks.some((b) => holds(b, t)))
    setLoaded(true)
    setErr(null)
  }, [id])

  /** One read at a time; a poke that lands mid-read earns exactly one more after it. */
  const refresh = useCallback((): Promise<void> => {
    if (flight.current) {
      again.current = true
      return flight.current
    }
    again.current = false
    flight.current = read()
      .catch(fail)
      .finally(() => {
        flight.current = null
        if (again.current) void refresh()
      })
    return flight.current
  }, [read, fail])

  const ask = useCallback(async () => {
    try {
      setStatus(await getSharedStatus(id))
    } catch (e) {
      fail(e)
      // Not knowing is not "still working": a failed ask would otherwise leave the last
      // "working" lit with no lease to clear it (review of 2026-10-17).
      setStatus({ ok: false, status: 'unknown', reachable: false })
    }
  }, [id, fail])

  useEffect(() => {
    void refresh()
    void ask()
  }, [refresh, ask])

  // Told, not polled: the cloud pokes this share when the agent starts or ends a turn.
  useEffect(
    () =>
      onAgentShare((sid, what) => {
        if (sid !== id) return
        if (what === 'revoked') {
          setGone(`${owner} took this agent back.`)
          return
        }
        // The reader took it off their own list — from another of their screens, say.
        if (what === 'declined') {
          setGone('You removed this agent from your list.')
          return
        }
        // Nobody took it back: the agent it was is gone (ended, or another now has its name).
        if (what === 'ended') {
          setGone('This agent was ended, and the share with it.')
          return
        }
        if (what === 'activity' || what === 'changed') {
          void refresh()
          void ask()
        }
      }),
    [id, owner, refresh, ask],
  )

  // The composer sent something: read now, and follow the bottom to it.
  useEffect(
    () =>
      onSharedKick(id, () => {
        stick.current = true
        void refresh()
        void ask()
      }),
    [id, refresh, ask],
  )

  // Back to the page after a while away: look again once.
  useEffect(() => {
    const on = () => {
      if (document.visibilityState !== 'visible') return
      void refresh()
      void ask()
    }
    document.addEventListener('visibilitychange', on)
    return () => document.removeEventListener('visibilitychange', on)
  }, [refresh, ask])

  const working = status?.status === 'active' && !gone
  const asking = !!status?.needs_input && !working && !gone
  // What the composer's Stop and the pill read.
  useEffect(() => {
    setSharedLive(id, { working, asking, gone })
  }, [id, working, asking, gone])
  useEffect(() => () => setSharedLive(id, { working: false, asking: false, gone: null }), [id])

  // A turn that ENDED is what brings the reply: read once when "working" goes out.
  const wasWorking = useRef(false)
  useEffect(() => {
    if (wasWorking.current && !working) void refresh()
    wasWorking.current = working
  }, [working, refresh])
  useEffect(() => {
    if (!working) return
    const t = setTimeout(() => {
      if (document.visibilityState !== 'visible') return
      void ask()
      void refresh()
    }, LEASE_MS)
    return () => clearTimeout(t)
  }, [working, status, ask, refresh])

  // Follow the bottom while the reader is there.
  useEffect(() => {
    const el = log.current
    if (el && stick.current) el.scrollTop = el.scrollHeight
  }, [blocks, echo, working])

  const olderBusy = useRef(false)
  const loadOlder = async () => {
    // One page at a time: a double tap (or a scroll and a tap) would prepend the same page twice.
    if (olderBusy.current || older.hidden || older.pending <= 0) return
    olderBusy.current = true
    try {
      const el = log.current
      const before = el?.scrollHeight ?? 0
      const file = at.current?.file
      const r = await getSharedTranscript(id, { before: older.from, tail: PAGE })
      // A page of a file this view no longer shows (a /clear in between) is not "earlier".
      if (!file || r.file !== at.current?.file) return
      setBlocks((b) => [...r.blocks, ...b])
      setOlder({ from: r.from, pending: r.pending, hidden: r.history_hidden })
      requestAnimationFrame(() => {
        if (el) el.scrollTop += el.scrollHeight - before
      })
    } catch (e) {
      fail(e)
    } finally {
      olderBusy.current = false
    }
  }

  const viewer: SharedViewer = useMemo(
    () => ({ selfEmail, selfLabel, ownerLabel: owner }),
    [selfEmail, selfLabel, owner],
  )
  const { resolveLabel, describeHash, describeRoom, resolutionKey } = useSenderResolution({ ownerName: owner })
  // The composer's search, over what this screen holds — no edge target: the machine's whole
  // conversation is not the friend's to search.
  const { items, hits, currentItem } = useCleanSearch(blocks, log, stick)

  // The turn still running, drawn under the agent's own head — Clean View's rule (AgentPanel).
  const tailIdx = items.length - 1
  const tailItem = items[tailIdx]
  const tail =
    working && tailItem && tailItem.kind === 'turn' && worthCollapsing(tailItem.blocks) && !turnSummary(tailItem.blocks)
      ? tailItem.blocks
      : null
  const [foldOpen, setFoldOpen] = useState(false)
  const foldBox = useRef<HTMLDivElement | null>(null)

  const common = {
    session: name,
    hash,
    resolve: resolveLabel,
    describeHash,
    describeRoom,
    ownerName: owner,
    resolutionKey,
  }

  return (
    <SharedViewContext.Provider value={viewer}>
      <aside id="claudeOverlay" className="shared-overlay" aria-label={`Conversation with ${name}, shared by ${owner}`}>
        <div
          id="claudeBody"
          role="log"
          aria-live="polite"
          ref={log}
          onScroll={(e) => {
            const el = e.currentTarget
            stick.current = el.scrollHeight - el.scrollTop - el.clientHeight < 60
            if (el.scrollTop < BACKFILL_TRIGGER_PX) void loadOlder()
          }}
        >
          {older.hidden && !share.history && <div className="pm-empty">Earlier conversation is not shared.</div>}
          {!older.hidden && older.pending > 0 && (
            <button type="button" className="pm-empty pm-loadmore" onClick={() => void loadOlder()}>
              …{older.pending} earlier — tap or scroll up to load
            </button>
          )}
          {!loaded && !err && !gone && <div className="pm-empty">loading transcript…</div>}
          {loaded && blocks.length === 0 && echo.length === 0 && !gone && <div className="pm-empty">Nothing said yet.</div>}
          {err && !gone && <div className="pm-empty sb-err">{err}</div>}
          {items.map((it, i) =>
            tail && i === tailIdx ? null : it.kind === 'said' ? (
              <Msg key={`s${i}`} b={it.block} {...common} hit={hits.has(it.block)} current={i === currentItem} />
            ) : (
              <Turn key={`t${i}`} blocks={it.blocks} {...common} hits={hits} current={i === currentItem} />
            ),
          )}
          {/* What the reader just sent, in the shape it will land in — framed as theirs, or as the
              sharer's words when they speak as the sharer — until the conversation shows it. */}
          {echo.map((t, i) => (
            <div className="sas-echo" key={`e${i}`}>
              <Msg
                b={{
                  kind: 'user',
                  text:
                    share.identity === 'owner'
                      ? t
                      : `<friend-msg from="${selfLabel}" email="${selfEmail ?? ''}">\n${t.replace(/<(\/?)friend-msg/gi, '‹$1friend-msg')}\n</friend-msg>`,
                }}
                {...common}
              />
            </div>
          ))}
          {working && (
            <div className={`live-wait${foldOpen ? ' open' : ''}`} aria-busy="true" ref={foldBox}>
              <Msg
                b={{ kind: 'assistant', text: '' }}
                {...common}
                belowHead={
                  tail ? (
                    <WorkingFold
                      blocks={tail}
                      working={tail}
                      {...common}
                      hits={hits}
                      current={tailIdx === currentItem}
                      open={foldOpen}
                      setOpen={setFoldOpen}
                      boxRef={foldBox}
                    />
                  ) : (
                    <span className="sas-working-line" role="status">
                      Working…
                    </span>
                  )
                }
              />
            </div>
          )}
          {asking && (
            <div className="sas-ask" role="status">
              {share.keys
                ? 'It is waiting for an answer in its terminal.'
                : `Waiting for ${owner} to answer it on ${machine}.`}
            </div>
          )}
          {status && !status.reachable && !gone && <div className="pm-empty">{machine} is not answering right now.</div>}
          {gone && (
            <p className="sas-gone" role="status">
              {gone}
            </p>
          )}
        </div>
        {/* Clean View's own foot line, and its manual way out of a stuck view: a tap reads again. */}
        <div
          className="ap-foot"
          onClick={() => {
            void refresh()
            void ask()
          }}
          title="Tap to refresh"
        >
          <Icon name="auto_awesome" size={13} />
          <span>
            {/* What this screen holds, never the conversation's whole length: with earlier
                conversation not shared, that number would say how much there was. */}
            Shared by {owner} · {product ? `${product} · ` : ''}
            {blocks.length} blocks
          </span>
        </div>
      </aside>
    </SharedViewContext.Provider>
  )
}
